export function formatAmount(value, currency = 'EUR', locale) {
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value ?? 0)
  } catch {
    return `${(value ?? 0).toFixed(2)} ${currency}`
  }
}

// ── Date display order ─────────────────────────────────────────────────────
// 'auto' keeps the locale's own order (what the app did before this setting);
// the others pin it, for people whose language and habits disagree.
export const DATE_FORMATS = ['auto', 'dmy', 'mdy', 'ymd']

// Components straight off the string: a date-only 'YYYY-MM-DD' through
// `new Date()` is parsed as UTC and would show the previous day west of
// Greenwich.
function dateParts(iso) {
  if (!iso) return null
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return y ? { y, m, d } : null
}

export function formatDay(iso, dateFormat = 'auto', locale) {
  const p = dateParts(iso)
  if (!p) return iso ?? ''
  const d = String(p.d).padStart(2, '0'), m = String(p.m).padStart(2, '0'), y = p.y
  if (dateFormat === 'dmy') return `${d}/${m}/${y}`
  if (dateFormat === 'mdy') return `${m}/${d}/${y}`
  if (dateFormat === 'ymd') return `${y}-${m}-${d}`
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    return new Intl.DateTimeFormat(lang, {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(p.y, p.m - 1, p.d))
  } catch {
    return `${d}/${m}/${y}`
  }
}

// Short-month style ("16 ott 2026"). An explicit order wins over the style:
// if the user pinned one, every date in the app follows it.
export function formatDate(iso, locale, dateFormat = 'auto') {
  if (dateFormat && dateFormat !== 'auto') return formatDay(iso, dateFormat, locale)
  const lang = locale ?? document.documentElement.lang ?? 'en'
  const p = dateParts(iso)
  if (!p) return iso
  try {
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric', month: 'short', day: '2-digit',
    }).format(new Date(p.y, p.m - 1, p.d))
  } catch {
    return iso
  }
}

export function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export function formatTime(iso, locale) {
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    return new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .format(new Date(iso))
  } catch {
    return ''
  }
}

export function weekdayShort(d, locale) {
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    // 3-letter abbreviation, uppercased to feel chip-like
    return new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(d).slice(0, 3).toUpperCase()
  } catch {
    return ''
  }
}

// Local-time YYYY-MM-DD key for grouping
export function dayKey(iso) {
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Short aliases used across Even pages.
export { formatAmount as fmtMoney, formatDate as fmtDate }
